import { environments } from '../../config/config.json';

/**
 * Tracker self invoking function
 *
 * @param {Record<string, any>} window
 * @param {Record<string, any>} document
 */
export default (function (window: Record<string, any>, document: Record<string, any>): void {
  // Collect data from query string of script tag src attribute
  let qsVars: { [key: string]: string } = {};
  const libScriptTag = document.getElementById('ra-events-lib');

  if (libScriptTag) {
    const scriptSrc = libScriptTag.getAttribute('src');
    // Extract queryString part of an url
    const queryString = scriptSrc && scriptSrc.split(/[?#]/)[1];

    if (queryString) {
      qsVars = queryString
        .split('&')
        .reduce((accumulator: { [key: string]: string }, pairs: string) => {
          const [name, value] = pairs.split('=');
          accumulator[decodeURIComponent(name)] = decodeURIComponent(value);

          return accumulator;
        }, {});
    }
  }

  // Global library namespace
  const namespace = (qsVars && qsVars.namespace) || 'raev';

  // Get initial stack before loading the library
  const initialStack = (window[namespace] && [...window[namespace]]) || [];

  // Add to global window as namespace defined by queryStrings in <script src /> or 'raev' by default
  window[namespace] = {
    apiKey: null,
    debug: false,
    endpoint: null,
    source: 'frontend',
    visitor: null,
    user: null,

    /**
     * config
     *
     * @param {string} apiKey
     * @param {'development' | 'test' | 'production'} stage
     * @param {boolean} debug (default: false)
     * @param {string} source (default: 'frontend')
     */
    config(
      apiKey: string,
      stage: 'development' | 'test' | 'production' = 'production',
      debug = false,
      source = 'events-lib',
    ) {
      this.apiKey = apiKey;
      this.debug = debug;
      this.source = source;
      this.endpoint =
        (environments[stage] && environments[stage].apiEndpoint) ||
        environments.production.apiEndpoint;

      return this;
    },

    /**
     * identify
     *
     * @param {'user' | 'visitor'} type
     * @param {number} id
     */
    identify(type: 'user' | 'visitor' = 'visitor', id: number): void {
      if (type === 'user') {
        this.user = id;
      } else if (type === 'visitor') {
        this.visitor = id;
      } else {
        this.error(`Unrecognized identity type ${type}`);
      }
    },

    /**
     * clear
     */
    clear(): void {
      this.user = null;
      this.visitor = null;
    },

    /**
     * log
     *
     * @param {any} message
     */
    log(message: any): void {
      if (!this.debug) {
        return;
      }

      console.log('[EventTracker]', message);
    },

    /**
     * error
     *
     * @param {any} message
     */
    error(message: any): void {
      console.error('[EventTracker]', message);
    },

    /**
     * push
     *
     * @param {Array<string>} data
     */
    push(data: Array<string> = []): void {
      const [method, ...parameters] = data;

      if (typeof this[method] === 'function') {
        this[method](...parameters);
      } else {
        this.error(`Invalid method '${method}' called`);
      }
    },

    /**
     * track
     *
     * @param {string} eventName
     * @param {Record<string, any>} eventdata
     * @param {Array<Object>} eventResources
     */
    track(
      eventName: string,
      eventData: Record<string, any>,
      eventResources: Array<Record<string, any>>,
    ): void {
      this.log(
        `action: track, data: ${JSON.stringify({
          eventName,
          eventData,
          eventResources,
        })}`,
      );
      this.send(this.build(eventName, eventData, eventResources));
    },

    /**
     * build
     *
     * @param {string} eventName
     * @param {Record<string, any>} eventdata
     * @param {Array<Object>} eventResources
     * @returns {Array<Record<string, any>>}
     */
    build(
      eventName: string,
      eventData?: Record<string, any>,
      eventResources?: Array<Record<string, any>>,
    ): Array<Record<string, any>> {
      const resources = [
        ...(this.user ? [{ type: 'user', fields: { id: this.user } }] : []),
        ...(this.visitor ? [{ type: 'visitor', fields: { id: this.visitor } }] : []),
        ...(eventResources || []),
      ];

      return [
        {
          eventSource: this.source,
          eventTime: new Date().toISOString(),
          eventName,
          eventResources: resources,
          ...(eventData ? { eventData } : null),
        },
      ];
    },

    /**
     * send
     *
     * @param {Array<Record<string, any>>} requestBody
     */
    send(requestBody: Array<Record<string, any>>): void {
      if (!requestBody || requestBody.length === 0) {
        this.error('No data to send to server');
        return;
      }

      if (!this.endpoint || !this.apiKey) {
        this.error('Invalid configuration provided for endpoint or api key');
        return;
      }

      const thisRef = this;
      const request = new XMLHttpRequest();

      request.onreadystatechange = function (): void {
        if (this.readyState === 4) {
          if (this.status === 200) {
            thisRef.log(`Request response: ${this.responseText}`);
          } else {
            const { error } = JSON.parse(this.responseText);

            thisRef.error(
              `Request error status ${this.status} with code '${error && error.code}'. ${
                error && error.detail
              }`,
            );
          }
        }
      };

      request.open('POST', thisRef.endpoint, true);
      request.setRequestHeader('Content-type', 'application/json;charset=UTF-8');
      request.setRequestHeader('x-api-key', thisRef.apiKey);
      request.send(JSON.stringify(requestBody));
    },
  };

  // Push each stack item into the library after it is initialised
  if (initialStack && initialStack.length) {
    initialStack.forEach((item) => {
      window[namespace].push(item);
    });
  }
})(window, document);
